import React, { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  FormControlLabel,
  Switch,
  Tooltip,
  Badge,
  TextField,
  useTheme,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  AutoAwesome as AIIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Inventory as ProductIcon,
  LocalShipping as ShippingIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PlayArrow as RunIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Security as SecurityIcon,
  Assessment as AssessmentIcon,
  Description as DocumentIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import complianceService from '../../services/complianceService';
import ocrService from '../../services/ocrService';
import api from '../../shared/services/api';
import { useTranslation } from '../../store/languageStore';

interface AIComplianceCheckerProps {
  onReportGenerated?: (report: any) => void;
}

const AIComplianceChecker: React.FC<AIComplianceCheckerProps> = ({ onReportGenerated }) => {
  const { t, tString } = useTranslation();
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [documentText, setDocumentText] = useState('');
  const [extractedData, setExtractedData] = useState<any>(null);
  const [complianceReport, setComplianceReport] = useState<any>(null);
  const [editMode, setEditMode] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    entities: true,
    products: true,
    transaction: false,
  });

  // AI Options
  const [aiOptions, setAIOptions] = useState({
    useSemanticMatching: true,
    extractEntitiesFirst: true,
    includeRiskAssessment: true,
  });

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    setUploadedFile(file);
    setActiveStep(1);
    setIsProcessing(true);

    try {
      // Step 1: Extract data from document using the same approach as /process page
      toast.info(tString('complianceChecker.toasts.processing'));

      // Upload the file and extract data using the working method from /process
      const formData = new FormData();
      formData.append('file', file);
      formData.append('module_id', 'compliance-check'); // Use a special module ID for compliance

      let extractedDocumentData: any = null;
      let documentContent = '';

      try {
        // Try to extract data from the document
        // This uses the same backend endpoint that works in /process
        const uploadResponse = await api.post('/files/upload-extract', formData);

        if (uploadResponse?.data?.extracted_data) {
          extractedDocumentData = uploadResponse.data.extracted_data;
          // Generate text content from extracted data for display
          documentContent = JSON.stringify(extractedDocumentData, null, 2);
        } else {
          throw new Error('No data extracted from document');
        }
      } catch (uploadError: any) {
        console.error('Document extraction failed:', uploadError);

        // If the standard extraction fails, we stop processing
        if (uploadError.code === 'ECONNABORTED' || uploadError.message?.includes('timeout')) {
          toast.error(tString('complianceChecker.toasts.timeout'));
        } else {
          toast.error(tString('complianceChecker.toasts.extractionFailed'));
        }
        setIsProcessing(false);
        setActiveStep(0);
        return;
      }

      if (documentContent && extractedDocumentData) {
        setDocumentText(documentContent);

        // Step 2: Extract entities from the already-extracted data
        toast.info(tString('complianceChecker.toasts.extracting'));
        
        // Extract entities and products from the data (same logic as /process page)
        const entities: any[] = [];
        const products: any[] = [];
        const countries: string[] = [];
        
        // Common entity field names in extracted data
        const entityFields = ['consignee', 'consignor', 'shipper', 'receiver', 
                             'nachrichtensender', 'nachrichtenempfanger', 
                             'company', 'customer', 'supplier', 'vendor'];
        const productFields = ['items', 'goods', 'products', 'description', 
                              'commodity', 'article', 'merchandise'];
        const countryFields = ['country', 'origin', 'destination', 
                              'destination_country', 'origin_country', 'ship_to'];
        
        // Extract entities
        entityFields.forEach(field => {
          if (extractedDocumentData[field]) {
            entities.push({
              name: extractedDocumentData[field],
              type: 'company',
              confidence: 0.9
            });
          }
        });
        
        // Extract products
        productFields.forEach(field => {
          if (extractedDocumentData[field]) {
            if (Array.isArray(extractedDocumentData[field])) {
              extractedDocumentData[field].forEach((item: any) => {
                products.push({
                  description: item.description || item.name || item,
                  hs_code: item.hs_code || item.hsCode || '',
                  origin_country: item.origin || 'Unknown'
                });
              });
            } else {
              products.push({
                description: extractedDocumentData[field],
                hs_code: '',
                origin_country: 'Unknown'
              });
            }
          }
        });
        
        // Extract countries
        countryFields.forEach(field => {
          if (extractedDocumentData[field] && !countries.includes(extractedDocumentData[field])) {
            countries.push(extractedDocumentData[field]);
          }
        });
        
        // Build extraction object compatible with existing code
        const extraction = {
          entities,
          products,
          transaction: {
            type: 'export',
            origin_country: countries[0] || 'US',
            destination_country: countries[1] || countries[0] || 'EU'
          },
          confidence: 0.85,
          extraction_time_ms: 500
        };

        setExtractedData(extraction);
        setActiveStep(2);
        toast.success(tString('complianceChecker.toasts.foundEntities', extraction.entities.length, extraction.products.length));
      }
    } catch (error) {
      console.error('Document processing failed:', error);
      toast.error(tString('complianceChecker.toasts.failed'));
    } finally {
      setIsProcessing(false);
    }
  }, [tString]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg'],
    },
    multiple: false,
  });

  const getDocumentType = (filename: string): string => {
    const lower = filename.toLowerCase();
    if (lower.includes('invoice')) return 'invoice';
    if (lower.includes('bill') && lower.includes('lading')) return 'bill_of_lading';
    if (lower.includes('packing')) return 'packing_list';
    if (lower.includes('certificate')) return 'certificate';
    return 'document';
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleRunCompliance = async () => {
    if (!documentText) {
      toast.error(tString('complianceChecker.toasts.noDocumentText'));
      return;
    }

    setIsProcessing(true);
    setActiveStep(3);

    try {
      toast.info(tString('complianceChecker.toasts.runningCheck'));

      let report;

      // Use the pre-extracted data for compliance check (same as /process page)
      const checkRequest = {
        entities: extractedData?.entities || [],
        products: extractedData?.products || [],
        countries: [
          extractedData?.transaction?.origin_country,
          extractedData?.transaction?.destination_country
        ].filter(Boolean),
        check_types: ['sanctions', 'license', 'dual_use'],
        document_id: uploadedFile?.name,
        fast_track: true,
        include_recommendations: true
      };

      try {
        // Use the same compliance check method as /process page
        report = await complianceService.checkCompliance(checkRequest);
      } catch (apiError: any) {
        console.error('Compliance check failed:', apiError);
        // Check if it's a timeout error
        if (apiError.code === 'ECONNABORTED' || apiError.message?.includes('timeout')) {
          toast.error(tString('complianceChecker.toasts.checkTimeout'));
        } else {
          toast.error(tString('complianceChecker.toasts.serviceUnavailable'));
        }
        setIsProcessing(false);
        return; // Don't show any fake results
      }

      setComplianceReport(report);
      setActiveStep(4);

      if (onReportGenerated) {
        onReportGenerated(report);
      }

      // Show result notification
      if (report.overall_status === 'compliant') {
        toast.success(tString('complianceChecker.toasts.checkPassed'));
      } else if (report.overall_status === 'non_compliant') {
        toast.error(tString('complianceChecker.toasts.issuesDetected'));
      } else {
        toast.warning(tString('complianceChecker.toasts.reviewRequired'));
      }
    } catch (error) {
      console.error('Compliance check failed:', error);
      toast.error(tString('complianceChecker.toasts.checkFailed'));
    } finally {
      setIsProcessing(false);
    }
  };

  const renderEntityCard = (entity: any, index: number) => (
    <Card key={index} sx={{ mb: 1 }}>
      <CardContent sx={{ py: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {entity.type === 'company' ? <BusinessIcon /> : <PersonIcon />}
            <Box>
              <Typography variant="subtitle2">{entity.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {entity.role || entity.type} {entity.country && `• ${entity.country}`}
              </Typography>
            </Box>
          </Box>
          {entity.confidence && (
            <Chip
              size="small"
              label={`${(entity.confidence * 100).toFixed(0)}%`}
              color={entity.confidence > 0.8 ? 'success' : 'warning'}
              variant="outlined"
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );

  const renderProductCard = (product: any, index: number) => (
    <Card key={index} sx={{ mb: 1 }}>
      <CardContent sx={{ py: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
            <ProductIcon color="action" />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2">{product.description}</Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                {product.hs_code && (
                  <Typography variant="caption" color="text.secondary">
                    HS: {product.hs_code}
                  </Typography>
                )}
                {product.quantity && (
                  <Typography variant="caption" color="text.secondary">
                    Qty: {product.quantity} {product.unit}
                  </Typography>
                )}
                {product.value && (
                  <Typography variant="caption" color="text.secondary">
                    ${product.value.toLocaleString()}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
          {product.dual_use_risk && (
            <Chip
              size="small"
              label={product.dual_use_risk}
              color={
                product.dual_use_risk === 'HIGH' ? 'error' :
                product.dual_use_risk === 'MEDIUM' ? 'warning' : 'default'
              }
              variant="outlined"
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'compliant': return 'success';
      case 'non_compliant': return 'error';
      case 'requires_review': return 'warning';
      default: return 'default';
    }
  };

  const getRiskColor = (level: string) => {
    switch (level) {
      case 'CRITICAL': return 'error';
      case 'HIGH': return 'error';
      case 'MEDIUM': return 'warning';
      case 'LOW': return 'success';
      case 'MINIMAL': return 'success';
      default: return 'default';
    }
  };

  return (
    <Box>
      {/* AI Options Bar */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: theme.palette.primary.light, color: theme.palette.primary.contrastText }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <AIIcon color="primary" />
          <Typography variant="h6" color="primary">
            {t('complianceChecker.title')}
          </Typography>
          <Chip
            icon={<SecurityIcon />}
            label={tString('complianceChecker.subtitle')}
            color="primary"
            variant="outlined"
            size="small"
          />
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={aiOptions.useSemanticMatching}
                  onChange={(e) => setAIOptions({ ...aiOptions, useSemanticMatching: e.target.checked })}
                  color="primary"
                />
              }
              label={
                <Tooltip title={tString('complianceChecker.options.semanticMatchingTooltip')}>
                  <span>{tString('complianceChecker.options.semanticMatching')}</span>
                </Tooltip>
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={aiOptions.extractEntitiesFirst}
                  onChange={(e) => setAIOptions({ ...aiOptions, extractEntitiesFirst: e.target.checked })}
                  color="primary"
                />
              }
              label={
                <Tooltip title={tString('complianceChecker.options.autoEntityExtractionTooltip')}>
                  <span>{tString('complianceChecker.options.autoEntityExtraction')}</span>
                </Tooltip>
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={aiOptions.includeRiskAssessment}
                  onChange={(e) => setAIOptions({ ...aiOptions, includeRiskAssessment: e.target.checked })}
                  color="primary"
                />
              }
              label={
                <Tooltip title={tString('complianceChecker.options.riskAssessmentTooltip')}>
                  <span>{tString('complianceChecker.options.riskAssessment')}</span>
                </Tooltip>
              }
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Stepper */}
      <Stepper activeStep={activeStep} orientation="vertical">
        <Step>
          <StepLabel>{t('complianceChecker.steps.uploadDocument')}</StepLabel>
          <StepContent>
            <Box
              {...getRootProps()}
              sx={{
                p: 4,
                border: '2px dashed',
                borderColor: isDragActive ? theme.palette.primary.main : theme.palette.grey[400],
                borderRadius: 2,
                bgcolor: isDragActive ? theme.palette.action.hover : theme.palette.background.paper,
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 0.3s',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  bgcolor: theme.palette.action.hover,
                },
              }}
            >
              <input {...getInputProps()} />
              <UploadIcon sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {isDragActive ? tString('complianceChecker.upload.dropHere') : tString('complianceChecker.upload.dragDrop')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('complianceChecker.upload.clickToSelect')}
              </Typography>
            </Box>
          </StepContent>
        </Step>

        <Step>
          <StepLabel>{t('complianceChecker.steps.documentProcessing')}</StepLabel>
          <StepContent>
            {isProcessing && activeStep === 1 ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <CircularProgress size={40} />
                <Typography variant="body2" sx={{ mt: 2 }}>
                  {t('complianceChecker.processing.withMistralOcr')}
                </Typography>
              </Box>
            ) : uploadedFile && (
              <Alert severity="success" icon={<CheckIcon />}>
                {t('complianceChecker.processing.success', uploadedFile.name)}
              </Alert>
            )}
          </StepContent>
        </Step>

        <Step>
          <StepLabel>{t('complianceChecker.steps.entityExtraction')}</StepLabel>
          <StepContent>
            {extractedData && (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('complianceChecker.extraction.aiExtracted', extractedData.entities.length, extractedData.products.length, (extractedData.confidence * 100).toFixed(0))}
                </Alert>

                {/* Entities Section */}
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}
                    onClick={() => toggleSection('entities')}
                    style={{ cursor: 'pointer' }}
                  >
                    <Typography variant="subtitle1" fontWeight="bold">
                      {t('complianceChecker.extraction.entities', extractedData.entities.length)}
                    </Typography>
                    <IconButton size="small">
                      {expandedSections.entities ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>
                  <Collapse in={expandedSections.entities}>
                    {extractedData.entities.map((entity: any, index: number) => 
                      renderEntityCard(entity, index)
                    )}
                  </Collapse>
                </Box>

                {/* Products Section */}
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}
                    onClick={() => toggleSection('products')}
                    style={{ cursor: 'pointer' }}
                  >
                    <Typography variant="subtitle1" fontWeight="bold">
                      {t('complianceChecker.extraction.products', extractedData.products.length)}
                    </Typography>
                    <IconButton size="small">
                      {expandedSections.products ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>
                  <Collapse in={expandedSections.products}>
                    {extractedData.products.map((product: any, index: number) =>
                      renderProductCard(product, index)
                    )}
                  </Collapse>
                </Box>

                {/* Transaction Details */}
                {extractedData.transaction && (
                  <Box>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}
                      onClick={() => toggleSection('transaction')}
                      style={{ cursor: 'pointer' }}
                    >
                      <Typography variant="subtitle1" fontWeight="bold">
                        Transaction Details
                      </Typography>
                      <IconButton size="small">
                        {expandedSections.transaction ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>
                    <Collapse in={expandedSections.transaction}>
                      <Card>
                        <CardContent>
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Type</Typography>
                              <Typography variant="body2">
                                {extractedData.transaction.transaction_type || 'N/A'}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Route</Typography>
                              <Typography variant="body2">
                                {extractedData.transaction.origin} → {extractedData.transaction.destination}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Total Value</Typography>
                              <Typography variant="body2">
                                ${extractedData.transaction.total_value?.toLocaleString() || '0'}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Reference</Typography>
                              <Typography variant="body2">
                                {extractedData.transaction.reference_number || 'N/A'}
                              </Typography>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Collapse>
                  </Box>
                )}

                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleRunCompliance}
                    disabled={isProcessing}
                    startIcon={<RunIcon />}
                  >
                    {t('complianceChecker.actions.runComplianceCheck')}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setEditMode(!editMode)}
                    startIcon={editMode ? <SaveIcon /> : <EditIcon />}
                  >
                    {editMode ? tString('common.save') : tString('common.edit')}
                  </Button>
                </Box>
              </Box>
            )}
          </StepContent>
        </Step>

        <Step>
          <StepLabel>{t('complianceChecker.steps.complianceAnalysis')}</StepLabel>
          <StepContent>
            {isProcessing && activeStep === 3 ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <CircularProgress size={40} />
                <Typography variant="body2" sx={{ mt: 2 }}>
                  {t('complianceChecker.processing.runningAnalysis')}
                </Typography>
                <LinearProgress sx={{ mt: 2 }} />
              </Box>
            ) : null}
          </StepContent>
        </Step>

        <Step>
          <StepLabel>{t('complianceChecker.steps.results')}</StepLabel>
          <StepContent>
            {complianceReport && (
              <Box>
                {/* Overall Status */}
                <Alert 
                  severity={getStatusColor(complianceReport.overall_status) as any}
                  sx={{ mb: 2 }}
                >
                  <Typography variant="h6">
                    Status: {complianceReport.overall_status.replace('_', ' ').toUpperCase()}
                  </Typography>
                </Alert>

                {/* Risk Assessment */}
                {complianceReport.risk_assessment && (
                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Risk Assessment
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Risk Score</Typography>
                          <Typography variant="h4">
                            {complianceReport.risk_assessment.overall_risk_score.toFixed(0)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Risk Level</Typography>
                          <Chip
                            label={complianceReport.risk_assessment.risk_level}
                            color={getRiskColor(complianceReport.risk_assessment.risk_level) as any}
                          />
                        </Grid>
                      </Grid>
                      
                      {complianceReport.risk_assessment.highest_risks && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>Highest Risks</Typography>
                          {complianceReport.risk_assessment.highest_risks.map((risk: string, idx: number) => (
                            <Alert severity="warning" key={idx} sx={{ mb: 1 }}>
                              {risk}
                            </Alert>
                          ))}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Recommendations */}
                {complianceReport.recommendations && complianceReport.recommendations.length > 0 && (
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        AI Recommendations
                      </Typography>
                      <List>
                        {complianceReport.recommendations.map((rec: string, idx: number) => (
                          <ListItem key={idx}>
                            <ListItemIcon>
                              <AssessmentIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText primary={rec} />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                )}

                <Box sx={{ mt: 3 }}>
                  <Button
                    variant="contained"
                    onClick={() => {
                      setActiveStep(0);
                      setUploadedFile(null);
                      setDocumentText('');
                      setExtractedData(null);
                      setComplianceReport(null);
                    }}
                  >
                    {t('complianceChecker.actions.checkAnother')}
                  </Button>
                </Box>
              </Box>
            )}
          </StepContent>
        </Step>
      </Stepper>
    </Box>
  );
};

export default AIComplianceChecker;